/**
 * Service layer — Checklist Template domains
 * Owns: Deliverable Types (tblfulsOyOyjOGXab) — milestone templates
 *       Drawing Sheet Templates (tblNWHEYC83Qcc2zX)
 *       Checklist Item Templates (tbl6SAgPLXMHm5Uja) — shared
 */

import { apiGetAll, apiGetOne, apiPatch, apiPost, apiDelete, apiDeleteBatch, apiPostBatch } from './_api.js'
import { CAT_FULL_KIT } from '../outline/constants.js'

const TBL_MS_TEMPLATES = 'tblfulsOyOyjOGXab'
const TBL_DS_TEMPLATES = 'tblNWHEYC83Qcc2zX'
const TBL_ITEMS        = 'tbl6SAgPLXMHm5Uja'
const TBL_LIVE_DELIV   = 'tblFuiDpahqCL4eIK'
const TBL_LIVE_ITEMS   = 'tblU6m44TpQ9zkpso'
const TBL_LIVE_SHEETS  = 'tblXFwxWKECjoVj9O'

const FIRM_SETTINGS_ID    = 'recNhIucrmsLAFuFU'
const FINANCIAL_ANALY_ID  = 'recNYPX6yx91T8Qxi'

/** Fetch all milestone templates, sorted alphabetically. */
export function getDeliverableTypes() {
  return apiGetAll(TBL_MS_TEMPLATES, 'sort%5B0%5D%5Bfield%5D=Name&sort%5B0%5D%5Bdirection%5D=asc')
}

/** Fetch all drawing sheet templates, sorted by sort order. */
export function getDrawingSheetTemplates() {
  return apiGetAll(TBL_DS_TEMPLATES, 'sort%5B0%5D%5Bfield%5D=Sheet+Template+Sort+Order&sort%5B0%5D%5Bdirection%5D=asc')
}

/** Fetch checklist item templates for a milestone template. */
export function getMilestoneTemplateItems(templateId) {
  const f = encodeURIComponent(`FIND("${templateId}",ARRAYJOIN({Record ID (from Project Deliverable Types)},","))`)
  return apiGetAll(TBL_ITEMS, `filterByFormula=${f}&sort%5B0%5D%5Bfield%5D=Sort+Order&sort%5B0%5D%5Bdirection%5D=asc`)
}

/** Fetch checklist item templates for a drawing sheet template. */
export function getDrawingTemplateItems(templateId) {
  const f = encodeURIComponent(`FIND("${templateId}",ARRAYJOIN({Record ID (from Drawing Sheet Template)},","))`)
  return apiGetAll(TBL_ITEMS, `filterByFormula=${f}&sort%5B0%5D%5Bfield%5D=Sort+Order&sort%5B0%5D%5Bdirection%5D=asc`)
}

/** Create a new milestone (Deliverable Type) template. Returns the created record. */
export function createDeliverableType({ name, phase, behavior, teamSize, quickLinks }) {
  const fields = {
    'Name':               name,
    'Phase':              phase ? [phase] : [],
    'Milestone Behavior': [behavior],
    'Firm Settings':      [FIRM_SETTINGS_ID],
    'Financial Analysis': [FINANCIAL_ANALY_ID],
  }
  const ts = parseFloat(teamSize)
  if (!isNaN(ts) && ts > 0) fields['Size of Team'] = ts
  if (quickLinks && quickLinks.length) fields['Quick Links'] = quickLinks
  return apiPost(TBL_MS_TEMPLATES, fields, { typecast: true })
}

/** Rename a milestone (Deliverable Type) template. Safe app-wide as of the 2026-09-22 id-
 *  conversion pass — every place that used to classify a milestone by this Name now matches on
 *  the type's own stable record id instead, so editing the Name here can't silently break
 *  anything. Returns the patched record. */
export function renameDeliverableType(id, name) {
  return apiPatch(TBL_MS_TEMPLATES, id, { 'Name': name })
}

/** Create a new drawing sheet template. Returns the created record. */
export function createDrawingSheetTemplate({ number, title, discipline, memberId }) {
  const fields = {
    'Sheet Number': number,
    'Sheet Name':   title,
    'Discipline':   discipline,
  }
  if (memberId) fields['Created By'] = [memberId]
  return apiPost(TBL_DS_TEMPLATES, fields)
}

/**
 * Save a checklist item template (patch for existing, post for new).
 * New items have an id starting with 'new-'. Returns the saved record.
 */
export async function saveTemplateItem(item, templateId, type) {
  if (item.id.startsWith('new-')) {
    return createTemplateItem(item, templateId, type)
  }
  const fields = {
    'Name':             item.name || '',
    'Summary':          item.summary || '',
    'Sort Order':       item.sortOrder,
    'Condition Question': item.conditionQuestion || null,
    'Condition Answer':   item.conditionAnswer   || null,
    'Condition Parent':   item.conditionParent   ? [item.conditionParent] : null,
  }
  if (type === 'Deliverable') {
    fields['Category'] = item.category
  }
  if (type === 'Drawing') {
    fields['Drawing Releases'] = (item.drawingReleases || [])
  }
  if (item._imagesDirty) {
    const existing = (item.exampleImages || []).map(a => ({ url: a.url }))
    const pending  = (item.pendingImages  || []).map(p => ({ url: typeof p === 'string' ? p : p.url }))
    fields['Example Images'] = existing.concat(pending)
  }
  return apiPatch(TBL_ITEMS, item.id, fields)
}

/** Create a new checklist item template (called internally by saveTemplateItem). */
async function createTemplateItem(item, templateId, type) {
  const fields = {
    'Name':       item.name,
    'Summary':    item.summary || '',
    'Sort Order': item.sortOrder,
    'Type':       type,
  }
  if (type === 'Deliverable') {
    fields['Category']                  = item.category || CAT_FULL_KIT
    fields['Project Deliverable Types'] = [templateId]
  } else {
    fields['Drawing Sheet Template'] = [templateId]
    fields['Drawing Releases']       = item.drawingReleases || []
  }
  if (item.conditionQuestion) fields['Condition Question'] = item.conditionQuestion
  if (item.conditionAnswer)   fields['Condition Answer']   = item.conditionAnswer
  if (item.conditionParent)   fields['Condition Parent']   = [item.conditionParent]
  return apiPost(TBL_ITEMS, fields)
}

/** Delete a checklist item template. */
export function deleteTemplateItem(id) {
  return apiDelete(TBL_ITEMS, id)
}

/** Patch only the Sort Order field — used by autosave after drag-reorder. */
export function patchItemSortOrder(id, sortOrder) {
  return apiPatch(TBL_ITEMS, id, { 'Sort Order': sortOrder })
}

/**
 * Analyze what Push to Live would do for a milestone template.
 * Returns { liveParents, newItems, orphaned }.
 */
export async function analyzePushMilestone(templateId, templateName, templateItems) {
  const nameEsc    = templateName.replace(/"/g, '\\"')
  const formula    = `AND({Deliverable}="${nameEsc}",NOT({Posted}),FIND("Executed",ARRAYJOIN({Contract Stage (from Project)},",")))`
  const liveParents = await apiGetAll(TBL_LIVE_DELIV, `filterByFormula=${encodeURIComponent(formula)}`)
  if (!liveParents.length) return { liveParents: [], newItems: [], orphaned: [] }
  const parentIds   = liveParents.map(r => r.id)
  const liveFormula = 'OR(' + parentIds.map(id => `FIND("${id}",ARRAYJOIN({Record ID (from Deliverable)},","))`).join(',') + ')'
  const liveItems   = await apiGetAll(TBL_LIVE_ITEMS, `filterByFormula=${encodeURIComponent(liveFormula)}`)
  return _diffItems(templateItems, liveItems, liveParents)
}

/** Execute Push to Live for a milestone template. */
export async function doPushMilestone(liveParents, newItems, orphanedIds) {
  if (orphanedIds.length) await apiDeleteBatch(TBL_LIVE_ITEMS, orphanedIds)
  const creates = []
  liveParents.forEach(parent => {
    newItems.forEach(ti => {
      creates.push({ 'Deliverable Checklist Item Template': [ti.id], 'Sort Order': ti.sortOrder, 'Deliverable': [parent.id] })
    })
  })
  if (creates.length) await apiPostBatch(TBL_LIVE_ITEMS, creates)
}

/**
 * Analyze what Push to Live would do for a drawing sheet template.
 * Returns { liveParents, newItems, orphaned }.
 */
export async function analyzePushDrawing(templateId, templateName, templateItems) {
  const nameEsc    = templateName.replace(/"/g, '\\"')
  const formula    = `AND({Drawing Sheet Template}="${nameEsc}",OR(FIND("In Progress",{Sheet Status}),FIND("Pending QC",{Sheet Status}),FIND("Revision Required",{Sheet Status})))`
  const liveParents = await apiGetAll(TBL_LIVE_SHEETS, `filterByFormula=${encodeURIComponent(formula)}`)
  if (!liveParents.length) return { liveParents: [], newItems: [], orphaned: [] }
  const parentIds   = liveParents.map(r => r.id)
  const liveFormula = 'OR(' + parentIds.map(id => `FIND("${id}",ARRAYJOIN({Record ID (from Drawing Sheet)},","))`).join(',') + ')'
  const liveItems   = await apiGetAll(TBL_LIVE_ITEMS, `filterByFormula=${encodeURIComponent(liveFormula)}`)
  return _diffItems(templateItems, liveItems, liveParents)
}

/** Execute Push to Live for a drawing sheet template. */
export async function doPushDrawing(liveParents, newItems, orphanedIds) {
  if (orphanedIds.length) await apiDeleteBatch(TBL_LIVE_ITEMS, orphanedIds)
  const creates = []
  liveParents.forEach(parent => {
    newItems.forEach(ti => {
      creates.push({ 'Deliverable Checklist Item Template': [ti.id], 'Sort Order': ti.sortOrder, 'Drawing Sheet': [parent.id] })
    })
  })
  if (creates.length) await apiPostBatch(TBL_LIVE_ITEMS, creates)
}

/** Shared diff logic: compare template items vs live items. */
function _diffItems(templateItems, liveItems, liveParents) {
  const templateItemIds  = templateItems.map(i => i.id)
  const liveTemplateIds  = liveItems.map(r => { const v = r.fields['Deliverable Checklist Item Template']; return Array.isArray(v) ? v[0] : (v || '') })
  const newItems = templateItems.filter(ti => !liveTemplateIds.includes(ti.id))
  const orphaned = liveItems.filter(li => {
    const v   = li.fields['Deliverable Checklist Item Template']
    const tid = Array.isArray(v) ? v[0] : (v || '')
    return tid && !templateItemIds.includes(tid)
  })
  return { liveParents, newItems, orphaned }
}
